// The callback queue (the call-handling parity, item 2), in the call screen's queue beside the call
// (BL-099): the calls still waiting on the office,
// oldest first, each saying why and who has it. "I'll call them back" makes it yours (the first to
// handle it); Open goes to the call to call back and record what happened; a missed call whose
// number called again, and got an outcome there, is one tap to say so. Nothing leaves by itself.
import { useState } from 'react';
import { RpcError } from '@flowology/client';
import { client } from '../api.ts';
import { useMirror } from '../shared/mirror.ts';
import { callWrite } from './reasons-model.ts';
import { phoneText } from '../types.ts';
import { laterWord, lookBackWord, olderThan, WHY, type Callback } from './callbacks-model.ts';
import { useDesign } from '../blueprint/design.ts';
import './callbacks.css';

export function Callbacks({
  calls,
  days,
  failed,
  whenOf,
  onOpen,
  onOlder,
  onChanged,
}: {
  /** null while it loads. */
  calls: Callback[] | null;
  days: number;
  failed: boolean;
  /** "2:14 p.m." today, "Oct 15, 2:14 p.m." before. */
  whenOf: (iso: string) => string;
  onOpen: (callId: string) => void;
  onOlder: (days: number) => void;
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const mirroring = useMirror();
  const bp = useDesign() === 'blueprint';
  const act = async (callId: string, patch: Record<string, unknown>) => {
    setBusy(callId);
    setError(null);
    try {
      await client.call(callWrite(mirroring), { id: callId, ...patch });
      onChanged();
    } catch (e) {
      setError(e instanceof RpcError ? e.message : 'That didn’t save. Try again.');
    } finally {
      setBusy(null);
    }
  };
  const older = olderThan(days);
  return (
    <section className="stone card call-backs" aria-label="Callbacks">
      <div className="row between wrap">
        <h2 className="h4">To call back</h2>
        <span className="muted small">{lookBackWord(days)}, oldest first</span>
      </div>
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      {failed && <p className="error">Couldn’t read the calls to call back.</p>}
      {!calls && !failed && <p className="muted">Reading the calls…</p>}
      {calls && !calls.length && <p className="muted">Nobody’s waiting on a call back.</p>}
      {calls && calls.length > 0 && (
        <ul className="call-backs-rows">
          {calls.map((c) =>
            bp ? (
              // Blueprint's row: who (it opens the call), when, why and who has it, then its one action
              <li key={c.callId} className="call-backs-row ib-cb">
                <button type="button" className="ib-cb-open" onClick={() => onOpen(c.callId)}>
                  {c.customerName ?? phoneText(c.from)}
                </button>
                <span className="t">{whenOf(c.startedAt)}</span>
                <span className="ib-sub">
                  <span className={`chip${c.why === 'missed' || c.why === 'voicemail' ? ' bad' : ''}`}>
                    {WHY[c.why]}
                  </span>
                  {c.customerName && <>, {phoneText(c.from)}</>}.{' '}
                  {c.takenBy ? `${c.takenBy} has it.` : 'Nobody has it yet.'}
                  {c.textedBackAt && <> Texted back {whenOf(c.textedBackAt)}.</>}
                </span>
                {c.laterCall && (
                  <span className="ib-sub">{laterWord(c.laterCall, whenOf(c.laterCall.startedAt))}</span>
                )}
                {(!c.takenBy || c.laterCall) && (
                  <span className="ib-cb-acts">
                    {!c.takenBy && (
                      <button
                        type="button"
                        className="ib-link"
                        disabled={busy === c.callId}
                        onClick={() => void act(c.callId, { claim: true })}
                      >
                        I’ll call them back
                      </button>
                    )}
                    {c.laterCall && (
                      <button
                        type="button"
                        className="ib-link"
                        disabled={busy === c.callId}
                        onClick={() => void act(c.callId, { handledOnCallId: c.laterCall!.callId })}
                      >
                        Handled on their later call
                      </button>
                    )}
                  </span>
                )}
              </li>
            ) : (
              <li key={c.callId} className="call-backs-row">
                <div className="call-backs-head">
                  <span className={`chip say${c.why === 'missed' || c.why === 'voicemail' ? ' warn' : ''}`}>
                    {WHY[c.why]}
                  </span>
                  <span className="t">{whenOf(c.startedAt)}</span>
                </div>
                <div className="call-backs-who">
                  <b>{c.customerName ?? phoneText(c.from)}</b>
                  {c.customerName && <span className="ink2"> · {phoneText(c.from)}</span>}
                </div>
                {c.textedBackAt && <div className="small">Texted back {whenOf(c.textedBackAt)}</div>}
                {c.laterCall && (
                  <div className="small">{laterWord(c.laterCall, whenOf(c.laterCall.startedAt))}</div>
                )}
                <div className="row wrap call-backs-acts">
                  <span className="muted small call-backs-has">
                    {c.takenBy ? `${c.takenBy} has it` : 'Nobody has it yet'}
                  </span>
                  {!c.takenBy && (
                    <button
                      type="button"
                      className="btn soft sm"
                      disabled={busy === c.callId}
                      onClick={() => void act(c.callId, { claim: true })}
                    >
                      I’ll call them back
                    </button>
                  )}
                  {c.laterCall && (
                    <button
                      type="button"
                      className="btn soft sm"
                      disabled={busy === c.callId}
                      onClick={() => void act(c.callId, { handledOnCallId: c.laterCall!.callId })}
                    >
                      Handled on their later call
                    </button>
                  )}
                  <button type="button" className="btn sm" onClick={() => onOpen(c.callId)}>
                    Open
                  </button>
                </div>
              </li>
            ),
          )}
        </ul>
      )}
      {older && (
        <button type="button" className={bp ? 'ib-link' : 'btn quiet'} onClick={() => onOlder(older)}>
          Show older
        </button>
      )}
    </section>
  );
}
